import { z } from 'zod';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import {
  dateInputSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
} from './common.js';

/**
 * How a shop PAYS its staff, as opposed to what it owes them for a particular garment.
 *
 * `payouts.ts` answers one question very well — this karigar finished these pieces at these
 * rates, so this is the cash. That is a piece-rate shop, and it is not every shop. A boutique
 * with three salaried tailors, or one that pays against recorded charges rather than counted
 * pieces, has no piece rate to settle against and nothing in that module to configure.
 *
 * So the CYCLE is the missing fact: the mode a shop pays on, the window it pays over, and the
 * date those windows are counted from. Everything else here hangs off it —
 *
 *  - an **advance** is cash handed over part-way through a cycle, deducted at the end of it;
 *  - an **add-on** is a bonus, an allowance or a reimbursement granted on top of what was
 *    earned;
 *  - a **run row** is one person's line when the cycle is worked out.
 *
 * The arithmetic is stated once, in {@link payrollPayablePaise}, in integer paise, so the screen
 * that shows an owner a figure before they pay it and the server that writes the run are running
 * the same line of code.
 *
 * It is deliberately a SIBLING of `payouts.ts` rather than a replacement. Every piece-rate
 * settlement already written keeps its shape and its meaning; the three fields this task adds to
 * `payoutSchema` are optional for exactly that reason.
 */

// --- Vocabulary ------------------------------------------------------------------------------

/** How a shop pays: per completed piece, per recorded charge, or a fixed wage for the cycle. */
export const PAYROLL_MODES = ['piecewise', 'charges', 'salary'] as const;
export type PayrollMode = (typeof PAYROLL_MODES)[number];
export const payrollModeSchema = z.enum(PAYROLL_MODES);

/** How long one cycle runs before it is paid out. */
export const PAYROLL_DURATIONS = ['daily', 'weekly', 'monthly'] as const;
export type PayrollDuration = (typeof PAYROLL_DURATIONS)[number];
export const payrollDurationSchema = z.enum(PAYROLL_DURATIONS);

export const PAYROLL_NOTE_MAX_LENGTH = 300;
export const PAYROLL_ADD_ON_REASON_MAX_LENGTH = 200;

/**
 * Why money was added on top of what the cycle earned.
 *
 * Required, and short-form validated rather than optional, for the same reason a payout
 * deduction's reason is: a figure a staff member cannot read the working for is the argument
 * this module exists to prevent. "₹2,000 extra" with nothing beside it is not an explanation.
 */
export const payrollAddOnReasonSchema = z
  .string()
  .trim()
  .min(3, 'Say what this is for')
  .max(PAYROLL_ADD_ON_REASON_MAX_LENGTH);

const payrollNoteSchema = z.string().trim().max(PAYROLL_NOTE_MAX_LENGTH);

// --- The arithmetic --------------------------------------------------------------------------

export interface PayrollPayableComponents {
  /** What the cycle's work came to, by whatever mode the shop is on. */
  earnedPaise: number;
  addOnPaise: number;
  advancePaise: number;
}

/** The one definition of what a staff member takes home, shared by the screen and the server. */
export function payrollPayablePaise(components: PayrollPayableComponents): number {
  return components.earnedPaise + components.addOnPaise - components.advancePaise;
}

// --- The cycle -------------------------------------------------------------------------------

/**
 * One configuration per shop: how it pays, over what window, counted from when.
 *
 * `active` rather than a delete, because switching payroll off must not take the cycles already
 * run out of the history with it — a shop that stops running payroll in March still has to be
 * able to show what it paid in February.
 */
export const payrollCycleSchema = z.object({
  id: objectIdSchema,
  mode: payrollModeSchema,
  duration: payrollDurationSchema,
  /** The anchor the windows are counted from, not the start of the current one. */
  cycleStartDate: isoDateStringSchema,
  active: z.boolean(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type PayrollCycle = z.infer<typeof payrollCycleSchema>;

/** Setting the configuration, or changing it. One per shop, so this writes rather than creates. */
export const upsertPayrollCycleSchema = z.object({
  mode: payrollModeSchema,
  duration: payrollDurationSchema,
  cycleStartDate: dateInputSchema,
  active: z.boolean().default(true),
});
export type UpsertPayrollCycleInput = z.input<typeof upsertPayrollCycleSchema>;

// --- Advances --------------------------------------------------------------------------------

/**
 * Money handed to a staff member part-way through a cycle, deducted from what they are paid at
 * the end of it.
 *
 * `staffName` is snapshotted onto the row for the same reason a payout snapshots it: somebody
 * who leaves in June does not blank out May's figures.
 */
export const payrollAdvanceSchema = z.object({
  id: objectIdSchema,
  staffUserId: objectIdSchema,
  staffName: z.string(),
  amountPaise: nonNegativePaiseSchema,
  paidAt: isoDateStringSchema,
  note: z.string().nullable(),
  recordedByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type PayrollAdvance = z.infer<typeof payrollAdvanceSchema>;

/** Recording one. `paidAt` defaults to now on the server — cash usually leaves as it is typed. */
export const recordPayrollAdvanceSchema = z.object({
  staffUserId: objectIdSchema,
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  paidAt: dateInputSchema.optional(),
  note: payrollNoteSchema.nullish(),
});
export type RecordPayrollAdvanceInput = z.input<typeof recordPayrollAdvanceSchema>;

// --- Add-ons ---------------------------------------------------------------------------------

/**
 * An ad-hoc positive adjustment applied to a cycle: a bonus, a festival allowance, a travel
 * reimbursement. Added to what is payable rather than folded into earnings, so the run row can
 * show it as its own column and the staff member can see where it came from.
 */
export const payrollAddOnSchema = z.object({
  id: objectIdSchema,
  staffUserId: objectIdSchema,
  staffName: z.string(),
  /** The cycle it lands in. An add-on outside a cycle has nothing to be added to. */
  payrollCycleId: objectIdSchema,
  amountPaise: nonNegativePaiseSchema,
  reason: z.string(),
  occurredAt: isoDateStringSchema,
  recordedByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type PayrollAddOn = z.infer<typeof payrollAddOnSchema>;

export const recordPayrollAddOnSchema = z.object({
  staffUserId: objectIdSchema,
  payrollCycleId: objectIdSchema,
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  reason: payrollAddOnReasonSchema,
  occurredAt: dateInputSchema.optional(),
});
export type RecordPayrollAddOnInput = z.input<typeof recordPayrollAddOnSchema>;

// --- What produced a figure (task 19) ---------------------------------------------------------

/**
 * WHY somebody was paid what they were.
 *
 * A shop reading a run row has to be able to tell "eleven blouses at ₹300" from "42 hours at
 * ₹90" from "₹30,000 a month, pro-rated across a week you attended five days of". Those are
 * three different arguments with a karigar, and a single `totalSalaryPaise` with nothing beside
 * it settles none of them. The basis names which one is being made.
 */
export const EARNING_BASES = ['piece', 'hourly', 'salary'] as const;
export type EarningBasis = (typeof EARNING_BASES)[number];
export const earningBasisSchema = z.enum(EARNING_BASES);

/**
 * The working behind one row's earnings.
 *
 * Every figure here is derived from STORED ATTENDANCE and the resolved rate — never from
 * anything a request said. `creditedDays` is deliberately not an integer: a half day is half a
 * day's salary, and rounding it either way would quietly overpay or underpay somebody every
 * cycle they came in late.
 */
export const payrollEarningBreakdownSchema = z.object({
  basis: earningBasisSchema,
  /** Attendance minutes summed across the cycle. 0 on a piece basis. */
  attendedMinutes: z.number().int().nonnegative(),
  /** Full days, plus half a day per half-day record. Capped at `cycleDays`. */
  creditedDays: z.number().nonnegative(),
  cycleDays: z.number().int().positive(),
  /** The hourly rate or monthly salary applied; `null` when the shop configured none. */
  appliedRatePaise: nonNegativePaiseSchema.nullable(),
  /** True when a monthly salary was scaled down because the cycle is shorter than the month. */
  prorated: z.boolean(),
});
export type PayrollEarningBreakdown = z.infer<typeof payrollEarningBreakdownSchema>;

// --- The run ---------------------------------------------------------------------------------

/**
 * One person's line when a cycle is worked out.
 *
 * `payablePaise` is the SIGNED figure. Somebody who drew more in advances than the cycle earned
 * is an ordinary state, and the negative is exactly what carries into the next cycle — clamping
 * it here (as `payoutNetPaise` deliberately does for a piece-rate settlement) would silently
 * forgive the difference.
 */
export const payrollRunRowSchema = z.object({
  staffUserId: objectIdSchema,
  staffName: z.string(),
  piecesDone: z.number().int().nonnegative(),
  /** What they earned in the window, by whatever mode the shop is on. */
  totalSalaryPaise: nonNegativePaiseSchema,
  advancePaise: nonNegativePaiseSchema,
  addOnPaise: nonNegativePaiseSchema,
  /** `totalSalary + addOn − advance`. See {@link payrollPayablePaise}. */
  payablePaise: paiseSchema,
  /**
   * The working behind `totalSalaryPaise` (task 19). Optional so every row shape task 13 and
   * task 2 shipped still parses; the run populates it on every row it produces, piece rows
   * included.
   */
  earning: payrollEarningBreakdownSchema.optional(),
});
export type PayrollRunRow = z.infer<typeof payrollRunRowSchema>;

/** The whole run: which window, on what terms, everybody's line, and the columns totalled. */
export const payrollRunSchema = z.object({
  mode: payrollModeSchema,
  duration: payrollDurationSchema,
  periodFrom: isoDateStringSchema,
  periodTo: isoDateStringSchema,
  rows: z.array(payrollRunRowSchema),
  totals: z.object({
    totalSalaryPaise: nonNegativePaiseSchema,
    advancePaise: nonNegativePaiseSchema,
    addOnPaise: nonNegativePaiseSchema,
    /** Can be negative for the same reason a row's can. */
    payablePaise: paiseSchema,
  }),
});
export type PayrollRun = z.infer<typeof payrollRunSchema>;

// --- Lists -----------------------------------------------------------------------------------

export const payrollAdvanceQuerySchema = paginationQuerySchema.extend({
  staffUserId: objectIdSchema.optional(),
  from: dateInputSchema.optional(),
  to: dateInputSchema.optional(),
});
export type PayrollAdvanceQuery = z.infer<typeof payrollAdvanceQuerySchema>;

export const payrollAdvancePageSchema = z.object({
  items: z.array(payrollAdvanceSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type PayrollAdvancePage = z.infer<typeof payrollAdvancePageSchema>;

export const payrollAddOnQuerySchema = paginationQuerySchema.extend({
  staffUserId: objectIdSchema.optional(),
  from: dateInputSchema.optional(),
  to: dateInputSchema.optional(),
});
export type PayrollAddOnQuery = z.infer<typeof payrollAddOnQuerySchema>;

export const payrollAddOnPageSchema = z.object({
  items: z.array(payrollAddOnSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type PayrollAddOnPage = z.infer<typeof payrollAddOnPageSchema>;
